export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  imageUrl: string;
  tags: string[];
}

export interface Category {
  slug: string;
  name: string;
  description: string;
  icon: string;
  articleCount: number;
}

export const categories: Category[] = [
  {
    slug: 'quick-start',
    name: '快速入门',
    description: '从零开始，快速掌握蓝色小导航的安装与基础用法',
    icon: 'ri-rocket-line',
    articleCount: 4,
  },
  {
    slug: 'layout-design',
    name: '布局设计',
    description: '探索导航栏在不同布局中的最佳实践与设计方案',
    icon: 'ri-layout-line',
    articleCount: 3,
  },
  {
    slug: 'custom-theme',
    name: '主题定制',
    description: '深度定制导航栏的配色、字体、间距与动画效果',
    icon: 'ri-palette-line',
    articleCount: 3,
  },
  {
    slug: 'responsive',
    name: '响应式适配',
    description: '让导航栏在桌面端、平板端和移动端完美呈现',
    icon: 'ri-smartphone-line',
    articleCount: 3,
  },
  {
    slug: 'advanced',
    name: '高级技巧',
    description: '进阶用法、性能优化与常见问题排查指南',
    icon: 'ri-tools-line',
    articleCount: 2,
  },
];

export const articles: Article[] = [
  {
    slug: 'getting-started-with-blue-nav',
    title: '五分钟上手蓝色小导航：从安装到第一个导航栏',
    excerpt: '本文将带你从零开始，一步步完成蓝色小导航的安装与配置，创建你的第一个导航栏组件。无需复杂的前置知识，跟随文档即可轻松完成。',
    content: `<p>蓝色小导航是一款专注于简洁与高效的导航组件，支持多种布局模式，适合各类网站项目。本文将帮助你快速完成安装与初始化配置。</p>
<h2>环境要求</h2>
<p>蓝色小导航基于纯 HTML、CSS 和 JavaScript 构建，不依赖任何第三方框架。你只需要一个现代浏览器即可开始使用。</p>
<h2>安装步骤</h2>
<p>第一步，通过 CDN 引入蓝色小导航的核心文件：</p>
<pre><code>&lt;link rel="stylesheet" href="https://cdn.bluenav.cn/css/blue-nav.min.css"&gt;
&lt;script src="https://cdn.bluenav.cn/js/blue-nav.min.js"&gt;&lt;/script&gt;</code></pre>
<p>第二步，在 HTML 中创建导航容器：</p>
<pre><code>&lt;nav class="bn-nav" id="mainNav"&gt;
  &lt;div class="bn-brand"&gt;我的网站&lt;/div&gt;
  &lt;ul class="bn-menu"&gt;
    &lt;li&gt;&lt;a href="#"&gt;首页&lt;/a&gt;&lt;/li&gt;
    &lt;li&gt;&lt;a href="#"&gt;产品&lt;/a&gt;&lt;/li&gt;
    &lt;li&gt;&lt;a href="#"&gt;关于&lt;/a&gt;&lt;/li&gt;
  &lt;/ul&gt;
&lt;/nav&gt;</code></pre>
<p>第三步，初始化导航组件：</p>
<pre><code>BlueNav.init('#mainNav', {
  theme: 'light',
  fixed: true,
  animated: true
});</code></pre>
<h2>基础配置项</h2>
<p>蓝色小导航提供了丰富的配置项，让你可以灵活控制导航栏的行为和外观。以下是几个最常用的配置参数：</p>
<ul>
<li><strong>theme</strong>：主题模式，可选 light、dark、auto</li>
<li><strong>fixed</strong>：是否固定在页面顶部</li>
<li><strong>animated</strong>：是否启用过渡动画</li>
<li><strong>breakpoint</strong>：移动端触发断点，默认 768px</li>
</ul>
<p>完成以上步骤后，刷新页面即可看到一个简洁优雅的蓝色导航栏出现在你的网站顶部。接下来你可以根据需要进一步配置颜色、间距等细节。</p>`,
    category: 'quick-start',
    author: '张小蓝',
    date: '2026-07-28',
    readTime: '5 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20web%20development%20workspace%20with%20laptop%20showing%20code%20editor%20and%20clean%20navigation%20bar%20design%2C%20soft%20blue%20ambient%20lighting%2C%20clean%20desk%20setup%20with%20coffee%20cup%2C%20editorial%20photography%2C%20high%20detail%2C%20professional%20tech%20atmosphere&width=800&height=480&seq=article-gs-01&orientation=landscape',
    tags: ['入门教程', '安装指南', '基础配置'],
  },
  {
    slug: 'horizontal-nav-best-practices',
    title: '水平导航栏设计指南：间距、对齐与视觉层次',
    excerpt: '合理的设计是导航栏成功的关键。本文深入探讨水平导航栏中的间距规则、对齐方式和视觉层次构建方法。',
    content: `<p>水平导航栏是网站中最常见的导航形式。一个设计精良的水平导航栏能显著提升用户的浏览效率。</p>
<h2>间距规则</h2>
<p>在蓝色小导航中，导航项之间的水平间距默认为 24px，这个数值经过大量用户测试验证，在可读性和空间利用之间取得了最佳平衡。对于密集型导航（菜单项超过 7 个），建议将间距调整为 16-20px。</p>
<h2>对齐策略</h2>
<p>常见的对齐方式有三种：左对齐、居中对齐和两端对齐。蓝色小导航默认采用左对齐布局，将品牌标识放在左侧，导航菜单紧随其后。这种布局符合用户从左到右的阅读习惯。</p>
<h2>视觉层次</h2>
<p>通过字体粗细、颜色深度和下划线指示器，蓝色小导航能够清晰地传达当前页面的位置。当前激活的导航项会以更深的蓝色和底部指示条突出显示。</p>
<h2>实际案例</h2>
<p>某电商平台在使用蓝色小导航后，其导航点击率提升了 23%。这得益于清晰的视觉层次和合理的间距设置，让用户能够快速定位目标页面。</p>`,
    category: 'layout-design',
    author: '李设计',
    date: '2026-07-25',
    readTime: '8 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20horizontal%20navigation%20bar%20design%20mockup%20on%20white%20background%2C%20minimalist%20UI%20design%20with%20blue%20accents%2C%20professional%20web%20design%20layout%2C%20editorial%20photography%2C%20clean%20composition&width=800&height=480&seq=article-hn-02&orientation=landscape',
    tags: ['布局设计', '水平导航', '用户体验'],
  },
  {
    slug: 'sidebar-navigation-patterns',
    title: '侧边栏导航模式详解：从固定侧栏到折叠菜单',
    excerpt: '侧边栏导航在后台管理系统和内容型网站中广泛应用。本文全面解析侧边栏导航的多种实现模式。',
    content: `<p>侧边栏导航适用于菜单项较多、层级较深的场景。蓝色小导航为侧边栏提供了完整的支持。</p>
<h2>固定侧边栏</h2>
<p>固定侧边栏始终保持在视口左侧，适合后台管理系统等需要频繁切换页面的场景。设置方式非常简单：</p>
<pre><code>BlueNav.init('#sidebarNav', {
  mode: 'sidebar',
  position: 'fixed',
  width: '240px'
});</code></pre>
<h2>可折叠侧边栏</h2>
<p>当屏幕空间有限时，可折叠侧边栏能自动收起，为内容区域腾出更多空间。蓝色小导航支持点击按钮或拖拽边缘来切换展开/折叠状态。</p>
<h2>多级菜单</h2>
<p>侧边栏天然适合展示多级菜单结构。蓝色小导航支持无限层级的嵌套菜单，并提供了流畅的展开/收起动画。</p>`,
    category: 'layout-design',
    author: '王前端',
    date: '2026-07-22',
    readTime: '7 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20dashboard%20sidebar%20navigation%20design%20with%20blue%20color%20theme%2C%20clean%20UI%20with%20icons%20and%20menu%20items%2C%20minimalist%20web%20app%20interface%2C%20professional%20design%20mockup&width=800&height=480&seq=article-sn-03&orientation=landscape',
    tags: ['侧边栏', '布局模式', '后台管理'],
  },
  {
    slug: 'color-customization-guide',
    title: '颜色定制完全指南：打造专属配色的导航栏',
    excerpt: '蓝色小导航提供灵活的颜色定制系统。无论是主色调、背景色还是悬停效果，你都可以自由调整。',
    content: `<p>颜色是品牌识别的重要组成部分。蓝色小导航提供了完善的颜色定制 API，让你能够轻松匹配品牌色。</p>
<h2>预设主题</h2>
<p>蓝色小导航内置了 8 套精心设计的预设主题，涵盖了从经典蓝到现代灰的多种风格。你可以通过一个简单的配置项快速切换：</p>
<pre><code>BlueNav.init('#myNav', {
  theme: 'ocean'
});</code></pre>
<h2>自定义颜色</h2>
<p>如果预设主题无法满足需求，你可以通过 CSS 变量或 JavaScript API 进行精细的颜色定制。支持修改以下颜色属性：背景色、文字色、悬停色、激活色、边框色和阴影色。</p>
<h2>暗色模式</h2>
<p>蓝色小导航内置了暗色模式支持，能够自动跟随系统主题切换，也支持手动控制。</p>`,
    category: 'custom-theme',
    author: '陈配色',
    date: '2026-07-20',
    readTime: '6 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Color%20palette%20design%20with%20various%20blue%20shades%20on%20a%20minimalist%20workspace%2C%20UI%20color%20customization%20interface%2C%20clean%20professional%20design%20tools%2C%20soft%20natural%20lighting&width=800&height=480&seq=article-cc-04&orientation=landscape',
    tags: ['颜色定制', '主题设计', '品牌风格'],
  },
  {
    slug: 'animation-effects-deep-dive',
    title: '动画效果深度解析：让导航栏的过渡更加流畅自然',
    excerpt: '精致的动画效果是提升用户体验的关键。本文详解蓝色小导航中各类动画的实现原理与调优技巧。',
    content: `<p>恰到好处的动画能让导航栏的交互体验提升一个档次。蓝色小导航内置了多种精心调校的动画效果。</p>
<h2>菜单展开动画</h2>
<p>下拉菜单的展开和收起使用了弹性缓动曲线，模拟真实物理世界的运动规律。动画时长默认设置为 300ms，这个时间既不会让用户感到等待，也能清晰地感知到变化。</p>
<h2>下划线滑动</h2>
<p>当前激活项的指示器采用了平滑的滑动动画，当用户在不同菜单项之间切换时，下划线会丝滑地移动到目标位置。</p>
<h2>移动端菜单</h2>
<p>移动端的汉堡菜单展开时采用从右滑入的方式，配合半透明遮罩层，营造出清晰的层次感。</p>
<h2>性能优化</h2>
<p>所有动画均使用 CSS transform 和 opacity 属性实现，充分利用 GPU 加速，确保在低性能设备上也能流畅运行。</p>`,
    category: 'custom-theme',
    author: '赵动画',
    date: '2026-07-18',
    readTime: '9 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Smooth%20UI%20animation%20demonstration%20with%20navigation%20transitions%2C%20abstract%20blue%20motion%20design%2C%20clean%20digital%20interface%20with%20flowing%20elements%2C%20modern%20tech%20aesthetic&width=800&height=480&seq=article-ae-05&orientation=landscape',
    tags: ['动画效果', '交互设计', '性能优化'],
  },
  {
    slug: 'responsive-nav-breakpoints',
    title: '响应式导航断点策略：在不同设备上呈现最佳体验',
    excerpt: '一套导航栏如何同时适配手机、平板和桌面端？本文分享蓝色小导航的响应式断点策略与实现方案。',
    content: `<p>响应式设计是现代 Web 开发的基石。蓝色小导航从设计之初就充分考虑了多设备适配的需求。</p>
<h2>断点设置</h2>
<p>蓝色小导航默认设置了三个响应式断点：768px（平板）、1024px（小桌面）和 1280px（大桌面）。在每个断点处，导航栏会自动调整布局模式。</p>
<h2>移动端策略</h2>
<p>在移动端（小于 768px），导航菜单会收起为汉堡图标，点击后以全屏面板的形式展开。触摸目标尺寸不小于 44px，符合 Apple 和 Google 的设计规范。</p>
<h2>平板端策略</h2>
<p>平板端（768px-1024px）采用精简菜单模式，优先显示最重要的导航项，次级项目收纳到"更多"下拉菜单中。</p>`,
    category: 'responsive',
    author: '孙适配',
    date: '2026-07-15',
    readTime: '7 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Responsive%20web%20design%20showing%20navigation%20on%20multiple%20devices%20desktop%20tablet%20mobile%2C%20clean%20blue%20theme%2C%20UI%20design%20mockups%2C%20professional%20tech%20presentation&width=800&height=480&seq=article-rb-06&orientation=landscape',
    tags: ['响应式设计', '断点策略', '移动端适配'],
  },
  {
    slug: 'mobile-hamburger-menu-guide',
    title: '移动端汉堡菜单完全指南：从图标到交互动效',
    excerpt: '汉堡菜单是移动端导航的核心交互模式。本文从图标设计、展开动画到手势操作，全面解析移动端导航设计。',
    content: `<p>汉堡菜单已成为移动端导航的事实标准。蓝色小导航的移动端菜单经过精心设计，兼顾美观与实用。</p>
<h2>图标设计</h2>
<p>蓝色小导航的汉堡图标由三条等宽横线组成，线条间距为 6px，在视觉上清晰可辨。点击后，三条线会优雅地变形为关闭图标（X）。</p>
<h2>菜单面板</h2>
<p>展开后的菜单面板覆盖整个屏幕，采用从右侧滑入的动画。背景使用半透明遮罩，菜单面板本身为白色，与品牌风格保持一致。</p>
<h2>手势支持</h2>
<p>除了点击汉堡图标，用户还可以通过向右滑动来打开菜单，向左滑动或点击遮罩区域来关闭菜单，提供了自然直观的操作体验。</p>`,
    category: 'responsive',
    author: '周移动',
    date: '2026-07-12',
    readTime: '6 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Mobile%20hamburger%20menu%20design%20on%20smartphone%20screen%2C%20clean%20blue%20navigation%20UI%2C%20modern%20app%20interface%20design%2C%20minimalist%20mobile%20layout%2C%20professional%20product%20photography&width=800&height=480&seq=article-hm-07&orientation=landscape',
    tags: ['移动端', '汉堡菜单', '手势交互'],
  },
  {
    slug: 'accessibility-nav-design',
    title: '无障碍导航设计：让每一位用户都能顺畅浏览',
    excerpt: 'Web 无障碍性是现代前端开发的重要议题。本文介绍如何使用蓝色小导航构建符合 WCAG 标准的无障碍导航。',
    content: `<p>无障碍设计不仅是对特殊用户群体的关怀，也是提升整体用户体验的重要实践。蓝色小导航严格遵循 WCAG 2.1 AA 级标准。</p>
<h2>键盘导航</h2>
<p>所有导航项均支持 Tab 键切换焦点，Enter 键激活链接。下拉菜单支持方向键导航，Escape 键关闭菜单。焦点指示器清晰可见，始终让用户知道当前所在位置。</p>
<h2>屏幕阅读器支持</h2>
<p>蓝色小导航使用了语义化的 HTML 标签和 ARIA 属性，确保屏幕阅读器能够准确解读导航结构。每个导航项都有明确的 role 和 aria-label 标注。</p>
<h2>色彩对比度</h2>
<p>所有文字与背景的色彩对比度均达到 4.5:1 以上（AAA 级标准），确保在各类显示设备和光线条件下都能清晰阅读。</p>`,
    category: 'responsive',
    author: '吴包容',
    date: '2026-07-10',
    readTime: '8 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Accessible%20web%20design%20concept%20with%20keyboard%20focus%20indicators%20and%20clean%20navigation%2C%20blue%20theme%2C%20inclusive%20design%20illustration%2C%20modern%20UI%20with%20accessibility%20features&width=800&height=480&seq=article-ac-08&orientation=landscape',
    tags: ['无障碍', 'WCAG', '键盘导航'],
  },
  {
    slug: 'mega-menu-implementation',
    title: '大型菜单实战：电商级导航栏的构建方案',
    excerpt: '当导航项超过 15 个时，传统的下拉菜单已经不够用了。本文将展示如何使用蓝色小导航构建电商级的大型菜单。',
    content: `<p>大型菜单（Mega Menu）是电商网站和内容平台的标配。蓝色小导航提供了完善的大型菜单组件。</p>
<h2>布局结构</h2>
<p>大型菜单采用多列网格布局，每列可以包含一个分类标题和若干子链接。蓝色小导航支持 2-5 列的灵活配置，列宽自动适配内容。</p>
<h2>图片与图标</h2>
<p>在大型菜单中，适当的图片和图标能够增强视觉吸引力。蓝色小导航支持在每个分类前添加小图标，以及在菜单右侧展示推荐产品的缩略图。</p>
<h2>性能考量</h2>
<p>大型菜单包含较多 DOM 节点，蓝色小导航采用了延迟渲染策略——菜单内容仅在首次展开时才进行渲染，减少了初始加载时间。</p>`,
    category: 'advanced',
    author: '郑架构',
    date: '2026-07-08',
    readTime: '10 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Mega%20menu%20dropdown%20design%20for%20ecommerce%20website%2C%20multi%20column%20navigation%20layout%2C%20clean%20blue%20theme%20UI%2C%20professional%20web%20design%20mockup%2C%20organized%20product%20categories&width=800&height=480&seq=article-mm-09&orientation=landscape',
    tags: ['大型菜单', '电商导航', '高级用法'],
  },
  {
    slug: 'performance-optimization',
    title: '导航栏性能优化：从 50KB 到 5KB 的极致瘦身',
    excerpt: '性能是用户体验的基石。本文分享蓝色小导航在包体积、渲染性能和网络加载方面的优化实践。',
    content: `<p>蓝色小导航的核心库在 gzip 压缩后仅 4.8KB，这得益于一系列精心设计的优化策略。</p>
<h2>Tree Shaking</h2>
<p>蓝色小导航采用模块化架构，支持按需引入。如果你只需要水平导航栏，可以只导入对应的模块，而无需加载整个库。</p>
<h2>CSS 优化</h2>
<p>样式文件使用了 CSS 自定义属性（变量），避免了重复的样式声明。未使用的 CSS 类会在构建时自动移除。</p>
<h2>渲染优化</h2>
<p>利用 requestAnimationFrame 和 CSS will-change 属性，蓝色小导航确保了动画帧率稳定在 60fps。在低端设备上，部分视觉效果会自动降级以保证流畅度。</p>
<h2>CDN 加速</h2>
<p>蓝色小导航的文件托管在全球 CDN 网络上，用户可以从最近的节点获取资源，大幅减少加载延迟。</p>`,
    category: 'advanced',
    author: '钱性能',
    date: '2026-07-05',
    readTime: '8 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Performance%20optimization%20dashboard%20showing%20web%20metrics%2C%20clean%20blue%20theme%2C%20code%20analysis%20interface%2C%20modern%20tech%20workspace%2C%20minimalist%20data%20visualization&width=800&height=480&seq=article-po-10&orientation=landscape',
    tags: ['性能优化', '包体积', '加载速度'],
  },
  {
    slug: 'nav-with-dropdown-menus',
    title: '下拉菜单设计模式：单级、多级与大型菜单的选择',
    excerpt: '下拉菜单是导航栏中最常见的子导航形式。了解不同下拉模式的特点，为你的项目选择最合适的方案。',
    content: `<p>蓝色小导航支持三种下拉菜单模式：单级下拉、多级级联和大型面板。每种模式适用于不同的场景。</p>
<h2>单级下拉</h2>
<p>最简单的下拉形式，点击或悬停父级菜单项时，垂直展示子菜单列表。适用于 5-8 个子项的简单导航场景。</p>
<h2>多级级联</h2>
<p>子菜单可以继续嵌套，形成级联结构。蓝色小导航支持最多 3 级嵌套，超出部分建议重新规划信息架构。</p>
<h2>大型面板</h2>
<p>当单个菜单分类下的内容过于丰富时，大型面板提供了更灵活的展示空间。可以同时展示文字链接、图标和推荐内容。</p>`,
    category: 'layout-design',
    author: '冯菜单',
    date: '2026-07-02',
    readTime: '7 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Dropdown%20menu%20design%20variations%20showing%20single%20level%20multi%20level%20and%20mega%20menu%2C%20clean%20blue%20UI%20components%2C%20professional%20design%20comparison%2C%20minimalist%20layout&width=800&height=480&seq=article-dm-11&orientation=landscape',
    tags: ['下拉菜单', '导航模式', 'UI 设计'],
  },
  {
    slug: 'nav-icon-system',
    title: '导航图标体系：图标的选用规范与最佳实践',
    excerpt: '合适的图标能让导航栏更加直观易用。本文分享蓝色小导航的图标选用规范和最佳实践。',
    content: `<p>图标是导航栏中不可或缺的视觉元素。蓝色小导航内置了一套精心挑选的图标集，覆盖了常见的导航场景。</p>
<h2>图标风格</h2>
<p>蓝色小导航采用线性图标风格，线条粗细统一为 1.5px，尺寸为 20x20px。这种风格的图标简洁清晰，与导航栏的整体设计语言保持一致。</p>
<h2>图标与文字</h2>
<p>图标应作为文字的辅助，而不是替代。蓝色小导航建议图标与文字组合使用——在桌面端同时显示图标和文字，在紧凑布局中可以考虑仅显示图标。</p>
<h2>自定义图标</h2>
<p>用户可以将自己的 SVG 图标替换默认图标。蓝色小导航支持内联 SVG 和图标字体两种方式，推荐使用内联 SVG 以获得更好的渲染效果。</p>`,
    category: 'custom-theme',
    author: '褚图标',
    date: '2026-06-28',
    readTime: '6 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Navigation%20icon%20set%20design%20with%20various%20UI%20icons%20in%20blue%20theme%2C%20clean%20grid%20layout%20of%20minimalist%20line%20icons%2C%20professional%20design%20elements%2C%20soft%20lighting&width=800&height=480&seq=article-is-12&orientation=landscape',
    tags: ['图标设计', '视觉规范', 'SVG'],
  },
  {
    slug: 'sticky-nav-vs-fixed-nav',
    title: '吸顶导航 vs 固定导航：两种模式的优劣与选择',
    excerpt: '吸顶导航和固定导航是两种最常见的导航定位方式。了解它们的区别和适用场景，做出明智的选择。',
    content: `<p>导航栏的定位方式直接影响用户的浏览体验。蓝色小导航同时支持吸顶（sticky）和固定（fixed）两种模式。</p>
<h2>吸顶导航</h2>
<p>吸顶导航在页面滚动到一定位置后固定在顶部。这种模式在初始状态下不占用屏幕空间，适合首屏内容丰富的页面。蓝色小导航的吸顶导航还支持滚动时缩小高度的效果，进一步节省空间。</p>
<h2>固定导航</h2>
<p>固定导航始终停留在页面顶部，用户在任何位置都能快速访问导航菜单。适合导航频率较高的场景，如后台管理系统。</p>
<h2>选择建议</h2>
<p>内容型网站推荐使用吸顶导航，工具型应用推荐使用固定导航。蓝色小导航也支持混合模式——桌面端固定、移动端吸顶，兼顾不同设备的体验需求。</p>`,
    category: 'quick-start',
    author: '卫定位',
    date: '2026-06-25',
    readTime: '6 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Comparison%20between%20sticky%20and%20fixed%20navigation%20bar%20on%20website%2C%20clean%20blue%20theme%2C%20web%20design%20mockup%2C%20professional%20UI%20layout%20comparison%2C%20minimalist%20style&width=800&height=480&seq=article-sv-13&orientation=landscape',
    tags: ['吸顶导航', '固定导航', '布局选择'],
  },
  {
    slug: 'breadcrumb-navigation-design',
    title: '面包屑导航设计：提升网站信息架构的层次感',
    excerpt: '面包屑导航是辅助用户理解当前位置和页面层级的重要组件。本文分享面包屑导航的设计规范。',
    content: `<p>面包屑导航（Breadcrumb）展示了用户从首页到当前页面的完整路径。蓝色小导航内置了面包屑组件。</p>
<h2>面包屑类型</h2>
<p>蓝色小导航支持三种面包屑样式：路径型（基于页面层级）、属性型（基于分类标签）和历史型（基于浏览记录）。其中路径型是最常用的形式。</p>
<h2>分隔符设计</h2>
<p>默认使用大于号（&gt;）作为层级分隔符，也支持斜杠（/）和箭头（→）。分隔符的颜色比文字更浅，形成清晰的视觉层次。</p>
<h2>响应式处理</h2>
<p>在移动端，面包屑路径可能过长。蓝色小导航会自动折叠中间层级，只显示首尾两个节点，并用省略号连接。</p>`,
    category: 'quick-start',
    author: '沈路径',
    date: '2026-06-22',
    readTime: '5 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Breadcrumb%20navigation%20design%20on%20clean%20web%20page%2C%20blue%20theme%20UI%20element%2C%20minimalist%20website%20header%2C%20professional%20design%20detail%2C%20path%20indicator%20component&width=800&height=480&seq=article-bc-14&orientation=landscape',
    tags: ['面包屑', '信息架构', '辅助导航'],
  },
  {
    slug: 'nav-search-integration',
    title: '搜索框与导航栏的融合：构建一体化的查找体验',
    excerpt: '将搜索功能融入导航栏是提升用户查找效率的有效方式。本文介绍搜索框与导航栏的多种融合方案。',
    content: `<p>蓝色小导航提供了搜索框集成方案，让用户无需离开导航栏即可进行全局搜索。</p>
<h2>内联搜索框</h2>
<p>最简单的集成方式是在导航栏右侧放置一个搜索输入框。蓝色小导航的搜索框采用圆角设计，与导航栏整体风格协调统一。</p>
<h2>可展开搜索</h2>
<p>在空间有限的场景中，搜索功能以图标形式展示，点击后展开为输入框。展开动画平滑自然，不会造成布局跳动。</p>
<h2>搜索建议</h2>
<p>输入时自动显示搜索建议下拉面板，建议内容可以通过 API 动态获取。面板样式与导航栏的下拉菜单保持一致。</p>`,
    category: 'quick-start',
    author: '杨搜索',
    date: '2026-06-18',
    readTime: '6 分钟',
    imageUrl: 'https://readdy.ai/api/search-image?query=Search%20bar%20integrated%20in%20navigation%20header%20design%2C%20clean%20blue%20theme%2C%20modern%20website%20UI%2C%20minimalist%20search%20interface%2C%20professional%20web%20design&width=800&height=480&seq=article-si-15&orientation=landscape',
    tags: ['搜索功能', '导航融合', '用户体验'],
  },
];

export const brandInfo = {
  name: '蓝色小导航',
  nameEn: 'BlueNav',
  slogan: '精致轻量的蓝色小导航解决方案',
  description: '蓝色小导航是一款简洁高效的轻量级导航组件，提供清晰的蓝色视觉风格与灵活的布局方案。无论是网站顶部导航、侧边栏还是移动端菜单，蓝色小导航都能快速集成、轻松定制，让您的页面结构更清晰、用户体验更流畅。',
  founded: '2024',
  email: 'hello@bluenav.cn',
  address: '中国 · 在线服务',
  values: [
    { title: '简洁至上', description: '我们相信优秀的导航设计应当是无形且直观的，让用户专注于内容而非导航本身。' },
    { title: '性能优先', description: '核心库体积控制在 5KB 以内，确保在任何设备和网络条件下都能快速加载。' },
    { title: '开放包容', description: '完全开源，社区驱动，欢迎每一位开发者参与到蓝色小导航的建设中来。' },
  ],
  testimonials: [
    {
      name: '陈工程师',
      role: '某互联网公司前端负责人',
      content: '蓝色小导航极大简化了我们的导航栏开发流程。之前需要花费数小时调整的细节，现在几分钟就能完成。代码体积小、性能出色，强烈推荐。',
      avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Asian%20tech%20professional%2C%20clean%20studio%20background%2C%20warm%20natural%20lighting%2C%20friendly%20expression%2C%20modern%20corporate%20headshot%20style&width=88&height=88&seq=testimonial-01&orientation=squarish',
    },
    {
      name: '林设计师',
      role: '独立 UI/UX 设计师',
      content: '作为一个设计师，我特别喜欢蓝色小导航的颜色定制系统。它能完美匹配我的设计稿，而且动画效果非常流畅自然。',
      avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Asian%20female%20designer%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20confident%20expression%2C%20modern%20creative%20headshot%20style&width=88&height=88&seq=testimonial-02&orientation=squarish',
    },
    {
      name: '黄创业者',
      role: '创业公司 CTO',
      content: '我们从零搭建官网时选择了蓝色小导航，团队反馈非常好。文档清晰，上手容易，而且响应式适配做得非常到位。',
      avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Asian%20male%20tech%20leader%2C%20clean%20studio%20background%2C%20warm%20lighting%2C%20approachable%20expression%2C%20modern%20corporate%20headshot%20style&width=88&height=88&seq=testimonial-03&orientation=squarish',
    },
  ],
  stats: [
    { value: '50,000+', label: '开发者使用' },
    { value: '3,200+', label: 'GitHub Stars' },
    { value: '99.8%', label: '浏览器兼容' },
    { value: '4.8KB', label: '核心体积' },
  ],
  faqs: [
    {
      question: '蓝色小导航是否免费使用？',
      answer: '是的，蓝色小导航完全开源免费，采用 MIT 许可证。无论您是个人开发者还是企业用户，都可以自由使用、修改和分发。',
    },
    {
      question: '蓝色小导航支持哪些浏览器？',
      answer: '蓝色小导航支持所有现代浏览器，包括 Chrome 90+、Firefox 88+、Safari 14+ 和 Edge 90+。兼容覆盖率达到 99.8%。',
    },
    {
      question: '如何将蓝色小导航集成到 React 项目中？',
      answer: '蓝色小导航提供了 React 封装组件，可通过 npm 安装。同时也支持直接在项目中使用原生版本，不受框架限制。请查看我们的快速入门教程了解详细步骤。',
    },
    {
      question: '蓝色小导航的动画效果会影响性能吗？',
      answer: '不会。所有动画均使用 GPU 加速的 CSS 属性（transform 和 opacity），在主流设备上均能保持 60fps 的流畅帧率。对于低性能设备，部分动画会自动降级。',
    },
    {
      question: '可以自定义导航栏的颜色吗？',
      answer: '完全可以。蓝色小导航提供了完善的 CSS 变量系统和 JavaScript API，支持自定义背景色、文字色、悬停色、激活色等所有颜色属性。还内置了 8 套预设主题供快速选择。',
    },
  ],
};

export function getArticleBySlug(slug: string): Article | undefined {
  return articles.find((a) => a.slug === slug);
}

export function getArticlesByCategory(categorySlug: string): Article[] {
  return articles.filter((a) => a.category === categorySlug);
}

export function getRelatedArticles(article: Article, count: number = 3): Article[] {
  const sameCategory = articles.filter(
    (a) => a.category === article.category && a.slug !== article.slug
  );
  const others = articles.filter(
    (a) => a.category !== article.category && a.slug !== article.slug
  );
  return [...sameCategory, ...others].slice(0, count);
}

export function getLatestArticles(count: number = 6): Article[] {
  return [...articles].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  ).slice(0, count);
}